// src/pages/inventory/ManageTransactions.js
import React, { useState, useEffect, Fragment, useRef } from "react";
import { Card, Col, Table, Input, CardBody, Button } from "reactstrap";
import { FormLabel, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Filter, X, Eye, User } from "react-feather";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getTransactionDetails,
  getTransactions,
  getWalletTransactions,
  walletTopUp,
  walletWithdraw,
} from "./SalesService";
import { useCurrency } from "../../_helper/CurrencyContext";
import {
  buildWalletTransactionReceiptHtml,
  openTransactionPrintWindow,
} from "../../utils/transactionPrint";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const Transactions = () => {
  const { getCurrencySymbol, allowedCurrencies, currency, convertCurrency } = useCurrency();

  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [searchValue, setSearchValue] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isWalking, setIsWalking] = useState("");
  const [isPending, setIsPending] = useState("");

  const { loading, setLoading } = useLoader();
  const { t } = useTranslation();
  const { pharmacyPOS } = usePharmacy();

  const eyeButtonRefs = useRef({});

  const [viewData, setViewData] = useState(null);
  const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
  const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
  const [viewTooltipOpen, setViewTooltipOpen] = useState({});
  const [customerTypeTooltipOpen, setCustomerTypeTooltipOpen] = useState({});
  const toggleCustomerTypeTooltip = () => setCustomerTypeTooltipOpen(!customerTypeTooltipOpen);
  const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
  const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);

  // Wallet modal state
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [walletForm, setWalletForm] = useState({
    amount: "",
    currencyType: "INR",
    note: "",
  });
  const [walletErrors, setWalletErrors] = useState({});

  const resetWalletForm = () => {
    setWalletForm({ amount: "", currencyType: "INR", note: "" });
    setWalletErrors({});
  };

  const validateWalletAmount = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const amount = Number(value);
    if (isNaN(amount) || amount <= 0) {
      return t("Please enter a valid amount greater than zero");
    }
    return "";
  };

  const closeTopupModal = () => {
    setShowTopupModal(false);
    resetWalletForm();
  };

  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
    resetWalletForm();
  };

  const toggleViewTooltip = (id) => {
    setViewTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const [totals, setTotals] = useState({
    totalDueAmount: 0,
    totalPaymentAmount: 0,
    netTotal: 0,
  } || {});

  const [showWallets, setShowWallets] = useState({});

  const [viewDataLoading, setViewDataLoading] = useState(false);

  // Fetch payments
  useEffect(() => {
    getPayments();
  }, [page, size]);

  const getPayments = async () => {
    try {
      setLoading(true);
      // const response = await getTransactions(body);
      const body = {
        page,
        limit: size,
      };
      const response = await getWalletTransactions(body);
      // console.log(response);
      // console.log(res);
      if (response.data.status === "success") {
        setPayments(response.data.data.walletTransactions || []);
        setTotals(response.data.data.wallets || {});
        const totalCount = response.data.data.total || 0;
        setTotalItems(Math.ceil(totalCount / size));
      }
    } catch (error) {
      setTotals({})
      console.error("Error fetching payments:", error);
      // toast.warning(t("Failed to fetch payments"));
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionDetails = async (actionType, invoiceId) => {
    try {
      setViewDataLoading(true);
      setLoading(true);
      const response = await getTransactionDetails({ actionType, invoiceId });
      if (response.data.status === "success") {
        return response.data.data;
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      toast.warning(t("Failed to fetch transaction details"));
    } finally {
      setViewDataLoading(false);
      setLoading(false);
    }
  };

  const fetchTransactionDetailsNew = async (payment) => {
    const body = {
      actionType: payment.actionType,
      invoiceId: payment.invoiceId,
    };
    const response = await getTransactionDetails(body);
    if (response.data.status === "success") {
      setViewData(response.data.data.details);
    }
  };

  const handleSearch = () => {
    setPage(1);
    getPayments();
  };

  const handleClear = () => {
    setSearchValue("");
    setFromDate("");
    setToDate("");
    setIsWalking("");
    setIsPending("");
    setPage(1);
    getPayments();
  };

  const handlePageChange = (pageNumber) => setPage(pageNumber);

  const handlePageSizeChange = (e) => {
    setSize(parseInt(e.target.value));
    setPage(1);
  };

  // derive filter applied status
  const isFilterApplied =
    (searchValue && searchValue.trim() !== "") ||
    (fromDate && fromDate.trim() !== "") ||
    (toDate && toDate.trim() !== "") ||
    (isWalking && isWalking.trim() !== "") ||
    (isPending && isPending.trim() !== "");

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString(undefined, { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`; // Example: May 07, 2025
  };

  const getTransactionTypeLabel = (actionType) => {
    if (actionType === "admin") return t("Admin Adjustment");
    if (actionType === "payin") return t("Wallet Topup");
    if (actionType === "payout") return t("Wallet Withdrawal");
    if (actionType === "SPayment") return t("Supplier Due Paid");
    if (actionType === "purchaseReturn") return t("Purchase Return");
    if (actionType === "payment") return t("Customer Receipt");
    if (actionType === "sale") return t("Sale");
    if (actionType === "purchase") return t("Purchase");
    if (actionType === "refund") return t("Refund");
    return t(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)}`);
  };

  const handlePrintTransaction = () => {
    if (!viewData) return;
    const details = viewData.detailedInfo?.details;
    const txDetails = viewData.detailedInfo?.transactionDetails;
    const docNo =
      details?.salesNumber ||
      details?.purchaseNumber ||
      details?.receiptNumber ||
      txDetails?.invoiceId ||
      "transaction";

    const html = buildWalletTransactionReceiptHtml(viewData, {
      pharmacyName:
        pharmacyPOS?.facility_name || pharmacyPOS?.hospital?.name || "",
      t,
      getCurrencySymbol,
      formatDate,
      getTransactionTypeLabel,
    });
    openTransactionPrintWindow(`Transaction_${docNo}`, html);
  };

  const getStatusBadge = (item) => {
    if (item.isSettled) {
      return (
        <div>
          {t("Status : ")}
          <span className="badge bg-success">{t("Settled")}</span>
        </div>
      );
    }
    if (item.isPaid) {
      return (
        <div>
          {t("Status : ")}
          <span className="badge bg-primary">{t("Paid")}</span>
        </div>
      );
    }
    if (item.isPartialPaid) {
      return (
        <div>
          {t("Status : ")}
          <span className="badge bg-warning">{t("Partial")}</span>
        </div>
      );
    }
    if (item.USDBalance > 0) {
      return (
        <div>
          {t("Status : ")}
          <span className="badge bg-danger">{t("Pending")}</span>
        </div>
      );
    }
    if (
      item.salesType === "return" ||
      item.isReturned ||
      item.purchaseType === "return"
    ) {
      return (
        <div>
          {t("Status : ")}
          <span className="badge bg-danger">{t("Return")}</span>
        </div>
      );
    }
    return null;
  };

  const formatAmount = (num) => {
    if (!num || num === 0) return "0.00"; // or just "0" if you prefer
    if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(2) + "B";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(3) + "K";
    return num.toFixed(2);
  };

  const formatBalanceAmount = (val) => {
    const num = Number(val) || 0;
    // Prevent -0.000 display for very small negative numbers
    return (Math.abs(num) < 0.0005 ? 0 : num).toFixed(3);
  };

  const toggleWallet = (key) => {
    setShowWallets((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleWalletInput = (e) => {
    const { name, value } = e.target;
    setWalletForm((p) => ({ ...p, [name]: value }));
    if (name === "amount" && walletErrors.amount) {
      const error = validateWalletAmount(value);
      if (!error) setWalletErrors((p) => ({ ...p, amount: "" }));
    }
  };

  const handleWalletAmountBlur = (e) => {
    const error = validateWalletAmount(e.target.value);
    if (error) setWalletErrors((p) => ({ ...p, amount: error }));
  };

  const renderWalletFormFields = () => (
    <>
      <div className="mb-2">
        <label className="form-label">{t("Amount")}</label>
        <input
          name="amount"
          className={`form-control ${walletErrors.amount ? "is-invalid" : ""}`}
          value={walletForm.amount}
          onChange={handleWalletInput}
          onBlur={handleWalletAmountBlur}
          type="number"
          min="0.01"
          step="0.01"
        />
        {walletErrors.amount && (
          <div className="text-danger small mt-1">{walletErrors.amount}</div>
        )}
      </div>
      <div className="mb-2">
        <label className="form-label">{t("Currency")}</label>
        <select name="currencyType" className="form-control" value={walletForm.currencyType} onChange={handleWalletInput}>
          {allowedCurrencies.map((cur) => (
            <option key={cur} value={cur}>
              {cur}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-2">
        <label className="form-label">{t("Note")}</label>
        <textarea name="note" className="form-control" value={walletForm.note} onChange={handleWalletInput} rows={2} />
      </div>
    </>
  );

  const submitWallet = async (type) => {
    const amount = Number(walletForm.amount);
    const amountError = !walletForm.amount?.toString().trim()
      ? t("Amount is required")
      : validateWalletAmount(walletForm.amount);

    if (amountError) {
      setWalletErrors({ amount: amountError });
      return;
    }

    const body = {
      amount: amount || 0,
      type,
      currencyType: walletForm.currencyType || "INR",
      note: walletForm.note || "",
    };

    try {
      setLoading(true);

      const resp = await (type === "payout"
        ? walletWithdraw(body)
        : walletTopUp(body)
      );
      const result = resp?.data || resp; // some axios wrappers auto-unpack

      if (result?.status === "success") {
        toast.success(result?.message || `${type} successful`);
        getPayments();
        setShowTopupModal(false);
        setShowWithdrawModal(false);
        resetWalletForm();
      } else {
        toast.warning(result?.message || "Operation failed");
      }

    } catch (err) {
      console.error("Wallet API error:", err);
      toast.warning("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const getCounterParty = (payment) => {
    const { actionType, customerType } = payment;

    // 1. Supplier Related
    if (["purchase", "purchaseReturn", "SPayment"].includes(actionType)) {
      return t("Supplier");
    }

    // 2. Wallet Related
    if (["payin", "payout", "admin"].includes(actionType)) {
      return t("Wallet");
    }

    // 3. Customer/Patient Related
    if (customerType === "hospital_patient") {
      return t("Hospital Patient");
    } else if (customerType === "inpatient") {
      return t("Existing Patient");
    } else if (customerType === "walking") {
      return t("Walk-in Patient");
    } else if (customerType === null && actionType === "payment") {
      return t("Existing Patient");
    } else {
      return t("Walk-in Patient");
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={t("Manage Transactions")}
        parent={t("Dashboard")}
        title={t("Transactions")}
      />

      <Col sm="12">
        <div className="mb-3 d-flex gap-2 justify-content-end">
          <Button color="primary" onClick={() => setShowTopupModal(true)}>{t("Topup Wallet")}</Button>
          <Button color="secondary" onClick={() => setShowWithdrawModal(true)}>{t("Withdraw")}</Button>
        </div>
        {/* Totals */}
        {!isFilterApplied && (
          <Row className="mt-4 g-3">
            {(() => {
              const totalItems = Object.keys(totals).length;
              const mdSize = totalItems > 0 ? Math.floor(12 / totalItems) : 12;

              return Object.entries(totals).map(([currencyCode, amount], index) => {
                const gradientBackground =
                  index % 2 === 0
                    ? "linear-gradient(135deg, #f1c40f, #f39c12)"
                    : "linear-gradient(135deg, #1abc9c, #16a085)";

                return (
                  !loading && (
                    <Col md={mdSize} key={currencyCode}>
                      <Card
                        className="border-0 shadow-lg rounded-4 wallet-card"
                        style={{ background: gradientBackground, color: "#fff" }}
                      >
                        <CardBody className="d-flex justify-content-between align-items-center p-4">
                          <div>
                            <h6 className="fw-light mb-1">{t(`${currencyCode} Wallet`)}</h6>

                            <h3 className="fw-bold mb-0">
                              {showWallets[currencyCode]
                                ? `${getCurrencySymbol(currencyCode)}${formatBalanceAmount(amount)}`
                                : "••••"}
                            </h3>
                          </div>

                          <button
                            className="btn btn-light btn-sm shadow-sm"
                            onClick={() => toggleWallet(currencyCode)}
                          >
                            <i
                              className={`fa ${showWallets[currencyCode] ? "fa-eye-slash" : "fa-eye"
                                } wallet-view-btn`}
                            />
                          </button>
                        </CardBody>
                      </Card>
                    </Col>
                  )
                  // : (
                  //   <Col md={mdSize} key={currencyCode}>
                  //     <Skeleton
                  //       height={150}
                  //       width={150}
                  //       rounded
                  //     />
                  //   </Col>

                  // )
                );
              });
            })()}
          </Row>
        )}

        {/* Transactions Table */}
        <Card>
          <CardBody>
            <div className="table-responsive">
              <Table bordered hover striped>
                <thead>
                  <tr>
                    <th>#</th>
                    {/* <th>{t("Patient")}</th> */}
                    {/* <th>{t("Pharmacy")}</th> */}
                    {/* <th>{t("Sales No")}</th>
										<th>{t("Net Total")}</th> */}
                    {/* <th>{t("Paid Amount")}</th> */}
                    <th>{t("Transaction Type")}</th>
                    <th>{t("Counter Party")}</th>
                    <th>{t("Credit")}</th>
                    <th>{t("Debit")}</th>
                    <th>{t("Balance")}</th> {/* Due or Balance */}
                    {/* <th>{t("Status")}</th> */}
                    <th>{t("Date")}</th>
                    <th>{t("Paid In")}</th>
                    <th>{t("Note")}</th>
                    <th className="text-center">{t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length > 0 ? (
                    payments.map((payment, idx) => (
                      <tr key={payment._id}>
                        <td>{(page - 1) * size + idx + 1}</td>
                        <td>
                          <strong
                            className={`${payment.actionType === "transfer"
                              ? "text-primary"
                              : payment.actionType === "return"
                                ? "text-warning"
                                : payment.actionType === "sale" ||
                                  payment.actionType === "payment" ||
                                  payment.actionType === "purchaseReturn" ||
                                  payment.actionType === "payin"
                                  ? "text-success"
                                  : payment.actionType === "refund" ||
                                    payment.actionType === "purchase" ||
                                    payment.actionType === "SPayment" ||
                                    payment.actionType === "payout"
                                    ? "text-danger"
                                    : payment.actionType === "admin"
                                      ? "text-secondary"
                                      : "text-muted"
                              }`}
                          >
                            {getTransactionTypeLabel(payment.actionType)}
                            {/* transaction made by */}
                            {/* {payment.customerType && (
                              <span className="badge badge-secondary ms-2" style={{ fontSize: "smaller" }}>{getCounterParty(payment)}</span>
                            )} */}
                          </strong>
                        </td>
                        <td>
                          {getCounterParty(payment)}
                        </td>
                        <th className="text-success fw-bold">
                          {payment.transactionType === "credit" ||
                            payment.transactionType === "medicine" ? (
                            <>
                              {getCurrencySymbol(payment.currencyType)}{formatBalanceAmount(payment.amount)}
                              {/* {payment.currencyType === "USD" ? (
                                <>
                                  <strong>
                                    ${formatBalanceAmount(payment.USDAmount)}
                                  </strong>
                                  <br />
                                  <small className="text-muted">
                                    {formatBalanceAmount(payment.ZiGAmount)}{" "}
                                    ZiG
                                  </small>
                                </>
                              ) : (
                                <>
                                  <strong>
                                    {formatBalanceAmount(payment.ZiGAmount)}{" "}
                                    ZiG
                                  </strong>
                                  <br />
                                  <small className="text-muted">
                                    ${formatBalanceAmount(payment.USDAmount)}
                                  </small>
                                </>
                              )} */}
                            </>
                          ) : (
                            "-"
                          )}
                        </th>
                        <th className="text-danger fw-bold">
                          {payment.transactionType === "debit" ? (
                            <>
                              {getCurrencySymbol(payment.currencyType)}{formatBalanceAmount(payment.amount)}
                              {/* {payment.currencyType === "USD" ? (
                                <>
                                  <strong>
                                    ${formatBalanceAmount(payment.USDAmount)}
                                  </strong>
                                  <br />
                                  <small className="text-muted">
                                    {formatBalanceAmount(payment.ZiGAmount)}{" "}
                                    ZiG
                                  </small>
                                </>
                              ) : (
                                <>
                                  <strong>
                                    {formatBalanceAmount(payment.ZiGAmount)}{" "}
                                    ZiG
                                  </strong>
                                  <br />
                                  <small className="text-muted">
                                    ${formatBalanceAmount(payment.USDAmount)}
                                  </small>
                                </>
                              )} */}
                            </>
                          ) : (
                            "-"
                          )}
                        </th>
                        <td className="fw-bold">
                          {getCurrencySymbol(payment.currencyType)}{formatBalanceAmount(payment.wallets[payment.currencyType])}
                        </td>
                        {/* <td>{formatBalanceAmount(payment.ZiGBalance)} ZiG</td> */}
                        <td>{formatDate(payment.createdAt)}</td>
                        <td>
                          <span className="fw-bold">{payment.currencyType}</span>
                        </td>
                        <td className="border-b-2" style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {payment.reason ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`note_tooltip-${payment._id}`}>{payment.reason}</Tooltip>}
                            >
                              <span>{payment.reason}</span>
                            </OverlayTrigger>

                          ) : ('-')}
                        </td>
                        <td className="text-center">
                          {!["admin", "payout", "payin"].includes(payment.actionType) ? (
                            <button
                              className="success-action-button me-1"
                              id={`ViewToolTip-${payment._id}`}
                              ref={(el) =>
                                (eyeButtonRefs.current[payment._id] = el)
                              }
                              onClick={async () => {
                                if (eyeButtonRefs.current[payment._id]) {
                                  eyeButtonRefs.current[payment._id].blur();
                                }
                                setViewTooltipOpen((prev) => ({
                                  ...prev,
                                  [payment._id]: false,
                                }));
                                const detailedData =
                                  await fetchTransactionDetails(
                                    payment.actionType === 'SPayment' ? 'purchase' : payment.actionType,
                                    payment.invoiceId
                                  );
                                if (detailedData) {
                                  setViewData({
                                    ...payment,
                                    detailedInfo: detailedData,
                                  });
                                }
                              }}
                            >
                              <i
                                className="fa fa-eye"
                                style={{ fontSize: "18px" }}
                              ></i>
                              <ToolTip
                                attrToolTip={{
                                  placement: "top",
                                  isOpen: viewTooltipOpen[payment._id] || false,
                                  target: `ViewToolTip-${payment._id}`,
                                  toggle: () => toggleViewTooltip(payment._id),
                                }}
                              >
                                <strong>{t("View")}</strong>
                              </ToolTip>
                            </button>
                          ) : ("-")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="p-3 text-center text-muted">
                        {t("No payments found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="mt-3 d-md-flex align-items-center justify-content-between">
              <div className="show-entries">
                <FormLabel>{t("Show")}&nbsp;</FormLabel>
                <select
                  value={size}
                  onChange={handlePageSizeChange}
                  className="form-control"
                  style={{ display: "inline", width: "auto" }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                &nbsp;<FormLabel>{t("entries")}</FormLabel>
              </div>
              <MyPagination
                totalPages={totalItems}
                currentPage={page}
                onPageChange={handlePageChange}
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* View Transaction Modal */}
      <Modal
        show={!!viewData}
        onHide={() => setViewData(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <div className="d-flex w-100 align-items-center justify-content-between pe-3">
            <Modal.Title>{t("Transaction Details")}</Modal.Title>
            {viewData && !viewDataLoading && (
              <Button color="primary" size="sm" onClick={handlePrintTransaction}>
                <i className="fa fa-print me-1" />
                {t("Print")}
              </Button>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          {viewDataLoading ? (
            <div className="p-4 text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">{t("Loading...")}</span>
              </div>
            </div>
          ) : viewData ? (
            <div>
              {/* Header Section */}
              <Row className="mb-4">
                <Col md="8">
                  <h5 className="mb-1 fw-bold text-primary">
                    {getTransactionTypeLabel(viewData.actionType)}
                  </h5>
                  <h6 className="mb-2 text-muted">
                    {t("Transaction No")}:{" "}
                    {viewData.detailedInfo?.details?.salesNumber ||
                      viewData.detailedInfo?.details?.purchaseNumber ||
                      viewData.detailedInfo?.details?.receiptNumber ||
                      viewData.detailedInfo?.transactionDetails?.invoiceId ||
                      "-"}
                  </h6>
                  <div>
                    {t("Date")}:{" "}
                    {formatDate(
                      viewData.detailedInfo?.details?.createdAt ||
                      viewData.detailedInfo?.details?.purchaseDate ||
                      viewData.detailedInfo?.transactionDetails?.createdAt
                    )}
                  </div>
                  {viewData.detailedInfo?.details?.patientName && (
                    <div>
                      <strong>{t("Patient")}:</strong>{" "}
                      {viewData.detailedInfo.details.patientName}
                    </div>
                  )}
                  {viewData.detailedInfo?.details?.supplierName && (
                    <div>
                      <strong>{t("Supplier")}:</strong>{" "}
                      {viewData.detailedInfo.details.supplierName}
                    </div>
                  )}
                  {viewData.actionType === "payin" || viewData.actionType === "payout" ? (
                    <div>
                      <strong>{t("Source")}:</strong> {t("Wallet")}
                    </div>
                  ) : null}
                  {viewData.detailedInfo?.details && (
                    <>{getStatusBadge(viewData.detailedInfo.details)}</>
                  )}
                </Col>
                <Col md="4" className="text-end">
                  {/* Dynamic Total Amount Display */}
                  <h5 className="fw-bold">
                    {viewData.actionType === "refund" ? t("Refund Amount") : t("Total Amount")}: {getCurrencySymbol(viewData.detailedInfo?.details?.currencyType || viewData.detailedInfo?.transactionDetails?.currencyType || 'INR')}
                    {(
                      viewData.detailedInfo?.details?.amount ||
                      viewData.detailedInfo?.details?.netTotal ||
                      viewData.detailedInfo?.details?.NetTotal ||
                      viewData.detailedInfo?.details?.totalPaid ||
                      0
                    ).toFixed(2)}
                  </h5>

                  {/* Display amounts in other currencies if available */}
                  {viewData.detailedInfo?.details?.currencyRates &&
                    Object.keys(viewData.detailedInfo.details.currencyRates).length > 0 && (
                      <div className="text-muted small">
                        {Object.keys(viewData.detailedInfo.details.currencyRates)
                          .filter(currency => currency !== viewData.detailedInfo.details.currencyType)
                          .map(currency => {
                            const rate = viewData.detailedInfo.details.currencyRates[currency];
                            const primaryAmount = viewData.detailedInfo?.details?.amount ||
                              viewData.detailedInfo?.details?.netTotal || 0;

                            const baseAmount =
                              viewData.detailedInfo.details.currency ===
                                viewData.detailedInfo.details.baseCurrency
                                ? primaryAmount
                                : viewData.detailedInfo.details.convertedToBase;

                            const convertedAmount = baseAmount * rate;

                            return (
                              <div key={currency}>
                                {getCurrencySymbol(currency)}{convertedAmount.toFixed(2)}
                              </div>
                            );
                          })}
                      </div>
                    )}

                  {viewData.detailedInfo?.details?.totalPaid !== undefined && (
                    <div className="text-success">
                      {t("Total Paid")}: {getCurrencySymbol(viewData.detailedInfo.details.currencyType || currency)}
                      {viewData.detailedInfo.details.totalPaid.toFixed(2)}
                    </div>
                  )}

                  {/* Dynamic Due Amount Display */}
                  {viewData.detailedInfo?.details?.balances && (
                    Object.entries(viewData.detailedInfo.details.balances).map(([currency, balance]) => (
                      balance > 0 && (
                        <div key={currency} className="text-danger">
                          {t("Due Amount")} ({currency}): {getCurrencySymbol(currency)}
                          {balance.toFixed(2)}
                        </div>
                      )
                    ))
                  )}
                </Col>
              </Row>

              {/* Items/Stocks Table */}
              {(viewData.detailedInfo?.details?.stocks?.length > 0 ||
                viewData.detailedInfo?.details?.items?.length > 0) && (
                  <div className="mb-4 table-responsive">
                    <Table bordered size="sm" className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>{t("Product/Brand")}</th>
                          <th>{t("Salt Name")}</th>
                          <th>{t("Batch No")}</th>
                          <th className="text-center">{t("Quantity")}</th>
                          <th className="text-center">{t("Unit Cost")}</th>
                          <th className="text-center">{t("Unit MRP")}</th>
                          <th className="text-center">{t("Total")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Handle stocks array (for purchase/return transactions) */}
                        {viewData.detailedInfo?.details?.stocks?.length > 0
                          ? viewData.detailedInfo.details.stocks.map(
                            (item, index) => {
                              {/* const currencySymbol = getCurrencySymbol(viewData.detailedInfo.details.currencyType || 'INR'); */ }
                              const currencySymbol = getCurrencySymbol();
                              return (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.brandName || "-"}</td>
                                  <td>{!item.isOTC ? item.medicineName : "-"}</td>
                                  <td>{item.batchNumber || "-"}</td>
                                  <td className="text-center">
                                    {item.totalMedicine || item.totalMedicineReturned || "-"}
                                  </td>
                                  <td className="text-center">
                                    {currencySymbol}
                                    {item.unitPrice?.toFixed(2)}
                                  </td>
                                  <td className="text-center">
                                    {currencySymbol}
                                    {item.unitPricePerMedicine?.toFixed(2)}
                                  </td>
                                  <td className="text-center fw-bold">
                                    {currencySymbol}
                                    {(viewData.detailedInfo?.details?.totalPrice || item.totalPrice || 0).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            }
                          )
                          : /* Handle items array (for sale/refund transactions) */
                          viewData.detailedInfo?.details?.items?.length > 0
                            ? viewData.detailedInfo.details.items.map(
                              (item, index) => {
                                {/* const currencySymbol = getCurrencySymbol(viewData.detailedInfo.details.currencyType || 'INR'); */ }
                                const currencySymbol = getCurrencySymbol();
                                return (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.brandName || "-"}</td>
                                    <td>{!item.isOTC ? item.medicineName : "-"}</td>
                                    <td>{item.batchNumber || "-"}</td>
                                    <td className="text-center">
                                      {item.medicineQunatity ||
                                        item.quantity ||
                                        item.totalMedicineReturned ||
                                        "-"}
                                    </td>
                                    <td className="text-center">
                                      {currencySymbol}
                                      {item.unitPrice?.toFixed(2)}
                                    </td>
                                    <td className="text-center">
                                      {currencySymbol}
                                      {item.unitPricePerMedicine?.toFixed(2)}
                                    </td>
                                    <td className="text-center fw-bold">
                                      {currencySymbol}
                                      {(
                                        item.totalPrice ||
                                        item.subTotal ||
                                        0
                                      ).toFixed(2)}
                                    </td>
                                  </tr>
                                );
                              }
                            )
                            : null}
                      </tbody>
                    </Table>
                  </div>
                )}

              {/* Transaction Summary - Only show if there are items/stocks */}
              {(viewData.detailedInfo?.details?.stocks?.length > 0 ||
                viewData.detailedInfo?.details?.items?.length > 0) && (() => {
                  const details = viewData?.detailedInfo?.details;
                  const netTotal = viewData.detailedInfo?.details?.netTotal || viewData.detailedInfo?.details?.NetTotal || 0;
                  const tax = viewData.detailedInfo?.details?.tax || 0;
                  const totalTaxableValue = viewData.detailedInfo?.details?.taxableAmount || netTotal - viewData.detailedInfo.details.tax;
                  const convertedTaxableValue = convertCurrency(totalTaxableValue, viewData.detailedInfo.details.currencyType, currency);
                  const convertedTax = convertCurrency(viewData.detailedInfo.details.tax, viewData.detailedInfo.details.currencyType, currency);
                  const convertedNetTotal = convertCurrency(viewData.detailedInfo.details.netTotal, viewData.detailedInfo.details.currencyType, currency);
                  const convertedTransportCost = convertCurrency(viewData.detailedInfo.details.transportCost, viewData.detailedInfo.details.currencyType, currency);
                  const convertedDiscount = convertCurrency(viewData.detailedInfo.details.discount, viewData.detailedInfo.details.currencyType, currency);

                  // const getDiscountAmount = () => {
                  //   if (viewData.detailedInfo.details.discountType === "percentage") {
                  //     const itemsTotal = viewData.detailedInfo.details.items.reduce((sum, item) => sum + item.totalPrice, 0);
                  //     const totalAmt = viewData.detailedInfo.details.netTotal - viewData.detailedInfo.details.tax;
                  //     const discountAmt = (totalAmt * viewData.detailedInfo.details.discount) / 100;
                  //     return discountAmt;
                  //   } else {
                  //     return viewData.detailedInfo.details.discount;
                  //   }
                  // }

                  const normalizedBalances = details?.balances
                    ? details.balances
                    : details?.balance
                      ? { [details.currencyType]: details.balance }
                      : null;
                  return (
                    <Row className="justify-content-end">
                      <Col md="6">
                        <Table bordered size="sm">
                          <tbody>
                            {/* Discount */}
                            {viewData.detailedInfo?.details?.discount > 0 && (
                              <tr>
                                <td className="fw-bold">{t("Discount")}</td>
                                <td className="text-end text-warning fw-bold">
                                  {`- (${viewData.detailedInfo.details.discount}%)`}
                                </td>
                              </tr>
                            )}

                            {/* Tax */}
                            {viewData.detailedInfo?.details?.tax > 0 && (
                              <>
                                <tr>
                                  <td className="fw-bold">{t("Total Taxable Value")}</td>
                                  <td className="text-end fw-bold">{getCurrencySymbol()}
                                    {/* {currency !== viewData.detailedInfo.details.currencyType ? (
                                      convertedTaxableValue.toFixed(2)
                                    ) : (
                                      totalTaxableValue.toFixed(2)
                                    )} */}
                                    {totalTaxableValue.toFixed(2)}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-bold">{t("Tax")}</td>
                                  <td className="text-end fw-bold">
                                    +{getCurrencySymbol()}
                                    {viewData.detailedInfo.details.tax.toFixed(2)}
                                  </td>
                                </tr>
                              </>
                            )}
                            {/* Net Total */}
                            {(viewData.detailedInfo?.details?.netTotal !==
                              undefined ||
                              viewData.detailedInfo?.details?.NetTotal !==
                              undefined) && (
                                <tr>
                                  <td className="fw-bold">{t("Grand Total")}</td>
                                  <td className="text-end fw-bold">
                                    <div className="d-flex flex-column">
                                      {currency !== viewData.detailedInfo.details.currencyType && (
                                        <span className="tw-text-[10px] text-muted">
                                          {getCurrencySymbol()}{convertCurrency(viewData.detailedInfo.details.netTotal, viewData.detailedInfo.details.currencyType, currency).toFixed(2)}
                                        </span>
                                      )}
                                      {getCurrencySymbol(viewData.detailedInfo.details.currencyType || currency)}
                                      {(
                                        viewData.detailedInfo?.details?.netTotal ||
                                        viewData.detailedInfo?.details?.NetTotal ||
                                        0
                                      ).toFixed(2)}
                                    </div>
                                  </td>
                                </tr>
                              )}

                            {/* Transport Cost */}
                            {viewData.detailedInfo?.details?.transportCost > 0 && (
                              <tr>
                                <td className="fw-bold">{t("Transport Cost")}</td>
                                <td className="text-end fw-bold">
                                  +{getCurrencySymbol(viewData.detailedInfo.details.currencyType || currency)}
                                  {viewData.detailedInfo.details.transportCost.toFixed(
                                    2
                                  )}
                                </td>
                              </tr>
                            )}

                            {/* Total Paid */}
                            {viewData.detailedInfo?.details?.totalPaid !==
                              undefined && (
                                <tr>
                                  <td className="fw-bold">{t("Total Paid")}</td>
                                  <td className="text-end text-success fw-bold">
                                    {getCurrencySymbol(viewData.detailedInfo.details.currencyType || currency)}
                                    {(viewData.detailedInfo.details.totalPaid || viewData.detailedInfo.details.NetTotal || viewData.detailedInfo.details.netTotal || 0).toFixed(
                                      2
                                    )}
                                  </td>
                                </tr>
                              )}
                            {/* Partial Payment History */}
                            {viewData.detailedInfo?.details?.isPartialPaid &&
                              viewData.detailedInfo?.details?.purchaseAmountHistory?.length > 0 && (
                                <>
                                  <tr>
                                    <td colSpan="2" className="fw-bold bg-light">
                                      {t("Payment History")}
                                    </td>
                                  </tr>

                                  {viewData.detailedInfo.details.purchaseAmountHistory.map((entry, index) => (
                                    <tr key={entry._id || index}>
                                      <td>
                                        {t("Paid On")}: {formatDate(entry.date)}
                                      </td>
                                      <td className="text-end text-success fw-bold">
                                        {getCurrencySymbol(viewData.detailedInfo.details.currencyType)}
                                        {entry.amount.toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              )
                            }
                            {/* Due Amount — using normalizedBalances */}
                            {normalizedBalances &&
                              Object.entries(normalizedBalances).map(([currency, balance]) =>
                                balance > 0 ? (
                                  <tr key={currency}>
                                    <td className="fw-bold">
                                      {t("Due Amount")} ({currency})
                                    </td>
                                    <td className="text-end text-danger fw-bold">
                                      {getCurrencySymbol(currency)}
                                      {balance.toFixed(2)}
                                    </td>
                                  </tr>
                                ) : null
                              )}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  )
                })()}

              {/* Transaction Info Section - For transactions without detailed items */}
              {!viewData.detailedInfo?.details?.stocks?.length &&
                !viewData.detailedInfo?.details?.items?.length &&
                viewData.detailedInfo?.transactionDetails && (
                  <div className="mb-4">
                    <h6 className="pb-2 border-bottom fw-bold">
                      {t("Transaction Information")}
                    </h6>
                    <Row>
                      <Col>
                        <p className="border-end">
                          <small>
                            {t("Transaction Type")}:&nbsp;
                          </small>
                          {getTransactionTypeLabel(
                            viewData.detailedInfo.transactionDetails.actionType
                          )}
                        </p>
                      </Col>
                      <Col>
                        <p className="border-end">
                          <small>
                            {t("Currency Type")}:&nbsp;
                          </small>
                          {
                            viewData.detailedInfo.transactionDetails
                              .currencyType
                          }
                        </p>
                      </Col>
                      <Col>
                        <p className="border-end">
                          <small>
                            {t("Transaction Method")}:&nbsp;
                          </small>
                          {viewData.detailedInfo.transactionDetails.transactionType
                            .charAt(0)
                            .toUpperCase() +
                            viewData.detailedInfo.transactionDetails.transactionType.slice(
                              1
                            )}
                        </p>
                      </Col>
                      {/* <Col md="6">
                        Dynamic Amount Display for Transaction Details
                        <p>
                          <strong>{t("Amount")}:</strong>{" "}
                          {getCurrencySymbol(viewData.detailedInfo.transactionDetails.currencyType || 'USD')}
                          {viewData.detailedInfo.transactionDetails.amount?.toFixed(2) || "0.00"}
                        </p>

                        Display wallet balances if available
                        {viewData.detailedInfo?.transactionDetails?.wallets && (
                          Object.entries(viewData.detailedInfo.transactionDetails.wallets).map(([currency, amount]) => (
                            <p key={currency}>
                              <strong>{t("Wallet Balance")} ({currency}):</strong>{" "}
                              {getCurrencySymbol(currency)}
                              {amount.toFixed(2)}
                            </p>
                          ))
                        )}

                        Display conversion info if applicable
                        {viewData.detailedInfo.transactionDetails.isConversion && (
                          <p>
                            <strong>{t("Conversion Rate")}:</strong>{" "}
                            {viewData.detailedInfo.details?.conversionRate || "1.00"}
                          </p>
                        )}
                      </Col> */}
                    </Row>
                  </div>
                )}

              {/* Notes/Reason Section */}
              {(viewData.detailedInfo?.transactionDetails?.reason ||
                viewData.detailedInfo?.details?.notes) && (
                  <Row className="mt-3">
                    <Col md="12">
                      <label className="fw-bold">
                        {viewData.detailedInfo?.transactionDetails?.reason
                          ? t("Reason")
                          : t("Notes")}
                        :
                      </label>
                      <div>
                        {viewData.detailedInfo?.transactionDetails?.reason ||
                          viewData.detailedInfo?.details?.notes}
                      </div>
                    </Col>
                  </Row>
                )}
            </div>
          ) : (
            <p className="text-center text-muted">{t("No data available")}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewData(null)}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showTopupModal} onHide={closeTopupModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("Topup Wallet")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderWalletFormFields()}
        </Modal.Body>
        <Modal.Footer>
          <Button color="primary" onClick={() => submitWallet("payin")}>{t("Submit")}</Button>
          <Button color="secondary" onClick={closeTopupModal}>{t("Cancel")}</Button>
        </Modal.Footer>
      </Modal>

      {/* Withdraw Modal */}
      <Modal show={showWithdrawModal} onHide={closeWithdrawModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("Withdraw Wallet")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderWalletFormFields()}
        </Modal.Body>
        <Modal.Footer>
          <Button color="primary" onClick={() => submitWallet("payout")}>{t("Submit")}</Button>
          <Button color="secondary" onClick={closeWithdrawModal}>{t("Cancel")}</Button>
        </Modal.Footer>
      </Modal>
    </Fragment >
  );
};

export default Transactions;
